import { useEffect, useState, type FormEvent } from "react";
import { useProfiles } from "../profiles/ProfileContext";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { CRM_TYPES } from "../lib/crm";
import { HIRING_TYPES } from "../lib/jobs";
import { listRules, createRule, setRuleEnabled, deleteRule, type AutomationRule } from "../lib/automation";

const BLANK = { name: "", contact_type: "", task_title: "", due_in_days: "" };

export function Automations() {
  const { active } = useProfiles();
  const isHiring = active ? HIRING_TYPES.includes(active.type) : false;
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() { setRules((await listRules()).rules); }
  useEffect(() => {
    if (!isHiring) { setLoading(false); return; }
    refresh().finally(() => setLoading(false));
  }, [isHiring]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.task_title.trim()) return;
    setBusy("new");
    try {
      await createRule({
        name: form.name,
        trigger: "contact_created",
        action: "add_task",
        config: {
          contact_type: form.contact_type || undefined,
          task_title: form.task_title,
          due_in_days: form.due_in_days ? Number(form.due_in_days) : undefined,
        },
      });
      setForm({ ...BLANK });
      setShowForm(false);
      await refresh();
    } finally { setBusy(null); }
  }

  async function toggle(r: AutomationRule) {
    setBusy(r.id);
    try { await setRuleEnabled(r.id, !r.enabled); await refresh(); } finally { setBusy(null); }
  }

  async function remove(r: AutomationRule) {
    setBusy(r.id);
    try { await deleteRule(r.id); await refresh(); } finally { setBusy(null); }
  }

  const ruleSentence = (r: AutomationRule) =>
    `When a new CRM contact${r.config.contact_type ? ` (${labelize(r.config.contact_type)})` : ""} is added, create a follow-up task “${r.config.task_title}”${r.config.due_in_days != null ? `, due in ${r.config.due_in_days} day${r.config.due_in_days === 1 ? "" : "s"}` : ""}.`;

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader
        title="Automations"
        subtitle="Rules that run for you — like auto follow-ups when a new lead is added."
        actions={isHiring ? <Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "New rule"}</Button> : undefined}
      />

      {!isHiring ? (
        <p className="text-sm text-muted">Switch to a company or recruiter profile to set up automations.</p>
      ) : (
        <>
          {showForm && (
            <Panel title="New automation">
              <form className="grid gap-3" onSubmit={create}>
                <input className={inputClass} placeholder="Rule name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required aria-label="Rule name" />
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted">When a new CRM contact is added…</p>
                <select className={inputClass} value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} aria-label="Contact type filter">
                  <option value="">…of any type</option>
                  {CRM_TYPES.map((t) => <option key={t} value={t}>…of type {labelize(t)}</option>)}
                </select>
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted">…create a follow-up task</p>
                <input className={inputClass} placeholder="Task title (e.g. Call within 24h)" value={form.task_title} onChange={(e) => setForm({ ...form, task_title: e.target.value })} required aria-label="Task title" />
                <input className={inputClass} placeholder="Due in N days (optional)" inputMode="numeric" value={form.due_in_days} onChange={(e) => setForm({ ...form, due_in_days: e.target.value })} aria-label="Due in days" />
                <Button type="submit" disabled={busy === "new" || !form.name.trim() || !form.task_title.trim()}>Create rule</Button>
              </form>
            </Panel>
          )}

          {loading ? (
            <p className="font-mono text-sm text-muted">Loading…</p>
          ) : rules.length === 0 ? (
            <p className="font-mono text-sm text-muted">No automations yet. Create a rule to save yourself busywork.</p>
          ) : (
            <ul className="grid gap-3">
              {rules.map((r) => (
                <li key={r.id} className="grid gap-2 rounded-card border border-line bg-surface p-4" data-testid="rule">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-bold">{r.name}</span>
                    <span className="flex items-center gap-2">
                      <button type="button" role="switch" aria-checked={r.enabled} aria-label={`Toggle ${r.name}`} disabled={busy === r.id} onClick={() => toggle(r)}
                        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${r.enabled ? "bg-accent" : "bg-line"} disabled:opacity-60`}>
                        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-all ${r.enabled ? "left-[22px]" : "left-0.5"}`} />
                      </button>
                      <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy === r.id} onClick={() => remove(r)}>Delete</button>
                    </span>
                  </div>
                  <p className="text-sm text-muted">{ruleSentence(r)}</p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
